/**
 * WHAT:
 *   Custom hook for PDF navigation functionality (previous/next).
 *
 * WHY:
 *   Encapsulates navigation logic for browsing through parsed and failed PDFs.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Hooks
 *
 * DEPENDENCIES:
 *   - React useCallback hook
 *   - validateParsedData utility function
 *   - TestTabState for state management
 *
 * RISKS:
 *   - NOTE: Navigation may fail if index is out of bounds
 *   - NOTE: State updates may cause re-renders if not properly memoized
 *   - WARN: Failed PDF navigation may not properly reset error state
 */

import type { TestTabState } from "../types";

import { useCallback } from "react";

/**
 * WHAT:
 *   Custom hook that provides PDF navigation functions.
 *
 * WHY:
 *   Centralizes navigation logic for better organization.
 *
 * INPUTS:
 *   - state: TestTabState — State object containing all state values and setters
 *
 * OUTPUT:
 *   Object with handlePreviousPdf and handleNextPdf functions.
 *
 * BEHAVIOR:
 *   - Navigates between PDFs in current list (parsed or failed)
 *   - Updates UI state for failed PDFs (shows raw text and error)
 *   - Prevents index from going out of bounds
 *
 * RISKS:
 *   - NOTE: State updates may cause race conditions if called rapidly
 *
 * @param state - State object containing all state values and setters
 *
 *
 * @returns {object} Object containing handleNextPdf and handlePreviousPdf functions
 *
 */
export const usePdfNavigation = (state: TestTabState) => {
  const {
    browseFailedOnly,
    currentPdfIndex,
    failedPdfs,
    parsedPdfs,
    setCurrentPdfIndex,
    setError,
    setRawData,
    setRawPdfText,
    setShowRawData,
    setUploadedFile,
  } = state;

  /**
   * WHAT:
   *   Navigates to previous PDF in the current list (parsed or failed).
   *
   * WHY:
   *   Allows users to browse through multiple PDFs during testing.
   *
   * BEHAVIOR:
   *   - Decrements current PDF index
   *   - Updates UI state for failed PDFs (shows raw text and error)
   *   - Prevents index from going below 0
   */
  const handlePreviousPdf = useCallback(() => {
    // NOTE: Get current list length based on browse mode
    const listLength = (browseFailedOnly ? failedPdfs : parsedPdfs).length;
    if (listLength === 0) {
      return;
    }
    // NOTE: Decrement index with floor at 0
    setCurrentPdfIndex((previous) => Math.max(0, previous - 1));
    // NOTE: For failed PDFs, update UI state with error information
    if (browseFailedOnly) {
      const item = failedPdfs[Math.max(0, currentPdfIndex - 1)];
      if (item) {
        setRawPdfText(item.raw_text ?? "⚠️ No PDF text extracted");
        setShowRawData(true);
        setError(item.error ?? "");
        setUploadedFile(
          new File([], item.file_name ?? "unknown.pdf", {
            type: "application/pdf",
          }),
        );
        setRawData(null);
      }
    }
  }, [
    browseFailedOnly,
    failedPdfs,
    parsedPdfs,
    currentPdfIndex,
    setCurrentPdfIndex,
    setRawPdfText,
    setShowRawData,
    setError,
    setUploadedFile,
    setRawData,
  ]);

  /**
   * WHAT:
   *   Navigates to next PDF in the current list (parsed or failed).
   *
   * WHY:
   *   Allows users to browse through multiple PDFs during testing.
   *
   * BEHAVIOR:
   *   - Increments current PDF index
   *   - Updates UI state for failed PDFs (shows raw text and error)
   *   - Prevents index from exceeding list length
   */
  const handleNextPdf = useCallback(() => {
    const list = browseFailedOnly ? failedPdfs : parsedPdfs;
    const listLength = list.length;
    if (listLength === 0) {
      return;
    }
    setCurrentPdfIndex((previous) => Math.min(listLength - 1, previous + 1));
    if (browseFailedOnly) {
      const newIndex = Math.min(listLength - 1, currentPdfIndex + 1);
      const item = failedPdfs[newIndex];
      if (item) {
        setRawPdfText(item.raw_text ?? "⚠️ No PDF text extracted");
        setShowRawData(true);
        setError(item.error ?? "");
        setUploadedFile(
          new File([], item.file_name ?? "unknown.pdf", {
            type: "application/pdf",
          }),
        );
        setRawData(null);
      }
    }
  }, [
    browseFailedOnly,
    failedPdfs,
    parsedPdfs,
    currentPdfIndex,
    setCurrentPdfIndex,
    setRawPdfText,
    setShowRawData,
    setError,
    setUploadedFile,
    setRawData,
  ]);

  return {
    handleNextPdf,
    handlePreviousPdf,
  };
};
